/**
 * @file evidenceOpen.ts
 * @brief Which Meeting Intel row has its evidence open: one at a time.
 *
 * @details
 * DES 05-10-2026: opening a row's "where it was said" closes any other. A tiny
 * shared store rather than context, so every row (Points, Decisions, Tasks,
 * Agenda) can join without a provider. Keys carry the meeting id, so the same
 * position in the next meeting does not open by itself.
 *
 * @references
 * - MeetingEvidenceInline.tsx, MeetingDetailView.tsx
 *
 * @author DES
 * @date 05-10-2026
 */

import { useSyncExternalStore } from "react";

let openKey: string | null = null;
const listeners = new Set<() => void>();
const setOpenKey = (k: string | null) => {
  openKey = k;
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** `[open, toggle, close]` for one row's evidence. */
export function useEvidenceOpen(key: string): [boolean, () => void, () => void] {
  const current = useSyncExternalStore(subscribe, () => openKey);
  const open = current === key;
  return [open, () => setOpenKey(open ? null : key), () => setOpenKey(null)];
}

